/**
 * Google Analytics 4 (GA4) 헬퍼 함수
 * 
 * GA4 page_view 이벤트를 안정적으로 전송하기 위한 유틸리티
 */

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

/**
 * GA4 Measurement ID
 * layout.tsx에서 사용하는 것과 동일한 ID 사용
 */
const GA4_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || "G-QDDY7REG7J";

/**
 * GA4가 로드되었는지 확인
 */
function isGA4Loaded(): boolean {
  if (typeof window === "undefined") return false;
  return typeof window.gtag === "function" && Array.isArray(window.dataLayer);
}

/**
 * GA4가 로드될 때까지 대기 (최대 5초)
 */
function waitForGA4(maxWait: number = 5000): Promise<boolean> {
  return new Promise((resolve) => {
    if (isGA4Loaded()) {
      resolve(true);
      return;
    }

    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (isGA4Loaded()) {
        clearInterval(checkInterval);
        resolve(true);
      } else if (Date.now() - startTime >= maxWait) {
        clearInterval(checkInterval);
        resolve(false);
      }
    }, 100);
  });
}

/**
 * GA4 page_view 이벤트 전송
 * 
 * @param options - page_view 이벤트 옵션
 * @param options.pageLocation - 페이지 전체 URL (예: https://tone-market.com/404)
 * @param options.pagePath - 페이지 경로 (예: /404)
 * @param options.pageTitle - 페이지 제목 (기본값: "404")
 * @param options.pageReferrer - 리퍼러 URL (선택사항)
 */
export async function sendGA4PageView(options: {
  pageLocation: string;
  pagePath: string;
  pageTitle?: string;
  pageReferrer?: string;
}): Promise<void> {
  if (typeof window === "undefined") {
    console.warn("[GA4] sendGA4PageView: window is undefined (SSR)");
    return;
  }

  // GA4가 로드될 때까지 대기
  const isLoaded = await waitForGA4(5000);
  
  if (!isLoaded) {
    console.warn("[GA4] sendGA4PageView: GA4 not loaded after timeout");
    // GA4가 로드되지 않아도 dataLayer에 푸시하여 나중에 처리되도록 함
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
    window.dataLayer.push({
      event: "page_view",
      page_location: options.pageLocation,
      page_path: options.pagePath,
      page_title: options.pageTitle || "404",
      page_referrer: options.pageReferrer || document.referrer || undefined,
    });
    return;
  }

  try {
    // 방법 1: gtag('event', 'page_view', {...}) - 권장
    window.gtag("event", "page_view", {
      page_location: options.pageLocation,
      page_path: options.pagePath,
      page_title: options.pageTitle || "404",
      page_referrer: options.pageReferrer || document.referrer || undefined,
    });

    // 방법 2: gtag('config', measurementId, {...}) - 추가 보장
    // 이 방법은 자동으로 page_view를 트리거합니다
    window.gtag("config", GA4_MEASUREMENT_ID, {
      page_location: options.pageLocation,
      page_path: options.pagePath,
      page_title: options.pageTitle || "404",
      page_referrer: options.pageReferrer || document.referrer || undefined,
    });

    console.log("[GA4] page_view sent:", {
      page_location: options.pageLocation,
      page_path: options.pagePath,
      page_title: options.pageTitle || "404",
    });
  } catch (error) {
    console.error("[GA4] sendGA4PageView error:", error);
  }
}

/**
 * 현재 페이지 정보를 기반으로 GA4 page_view 전송
 * 
 * @param customTitle - 커스텀 페이지 제목 (선택사항)
 */
export async function sendGA4PageViewForCurrentPage(
  customTitle?: string
): Promise<void> {
  if (typeof window === "undefined") return;

  await sendGA4PageView({
    pageLocation: window.location.href,
    pagePath: window.location.pathname + window.location.search,
    pageTitle: customTitle || document.title,
    pageReferrer: document.referrer || undefined,
  });
}

